import { ExpandMoreOutlined } from '@mui/icons-material';
import {
  Box,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { countries, getEmojiFlag, TCountryCode } from 'countries-list';
import { FC } from 'react';
import { MobileFieldProps } from 'src/models/FieldProps.interface';

const MobileField: FC<MobileFieldProps> = ({
  id,
  label,
  formikProps,
  sx,
  height = '40px',
  width = '450px',
  widthPercent = '30%',
  fieldName = 'phoneNumber',
}) => {
  const countryCodes = Object.keys(countries) as TCountryCode[];

  const sortedCountryCodes = countryCodes.sort((a, b) =>
    countries[a].name.localeCompare(countries[b].name)
  );

  return (
    <Box mb={0}>
      <Typography
        fontFamily="Outfit"
        fontWeight="400"
        fontSize="16px"
        color="var(--primary-navy)"
        mb={2}
      >
        {label}
      </Typography>
      <FormControl
        fullWidth
        error={
          formikProps.touched[fieldName] &&
          Boolean(formikProps.errors[fieldName])
        }
        sx={{ mb: 3 }}
      >
        <OutlinedInput
          id={id}
          aria-label="Phone Number"
          startAdornment={
            <Select
              labelId="countryCode-country-code-select-label"
              id="countryCodeCountryCode"
              value={formikProps.values.countryCode}
              IconComponent={ExpandMoreOutlined}
              onChange={(event) =>
                formikProps.setFieldValue('countryCode', event.target.value)
              }
              sx={{
                height: height,
                width: widthPercent,
                bgcolor: 'var(--text-bg)',
                border: 'none',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  fontFamily: 'Outfit',
                  fontSize: '16px',
                  paddingLeft: '2px',
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '200px',
                    width: '120px',
                    backgroundColor: 'var(--bg-color)',
                    overflowY: 'auto',
                  },
                  sx: {
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'var(--bg-color)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'var(--loader-color)',
                      borderRadius: '4px',
                      height: '30px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      backgroundColor: 'var(--primary-blue)',
                      cursor: 'pointer',
                    },
                  },
                },
              }}
            >
              {sortedCountryCodes.map((code) => {
                const phoneCodes = countries[code].phone;

                return phoneCodes.map((phoneCode, index) => (
                  <MenuItem
                    key={`${code}-${index}`}
                    value={phoneCode}
                    sx={{
                      fontFamily: 'Outfit',
                      fontSize: '16px',
                      '&.Mui-selected': {
                        backgroundColor: 'var(--inactive-btn)',
                        color: 'var(--primary-blue)',
                      },
                    }}
                  >
                    <span style={{ position: 'absolute', left: '-9999px' }}>
                      {phoneCode}
                    </span>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ fontSize: '20px', marginRight: 1 }}>
                        {getEmojiFlag(code)}
                      </Box>
                      +{phoneCode}
                    </Box>
                  </MenuItem>
                ));
              })}
            </Select>
          }
          {...formikProps.getFieldProps(fieldName)}
          sx={{
            height: height,
            width: width,
            bgcolor: 'var(--text-bg)',
            border: 'none',
            borderRadius: 1,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '& input': {
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            overflow: 'hidden',
            '& input:-webkit-autofill': {
              bgcolor: 'var(--bg-color)',
              WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-moz-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-ms-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            ...sx,
          }}
        />
        {formikProps.touched[fieldName] && formikProps.errors[fieldName] ? (
          <FormHelperText
            sx={{
              fontFamily: 'Outfit',
              fontWeight: '400',
              fontSize: '14px',
              color: 'var(--error)',
              display: 'flex',
              alignItems: 'center',
              ml: 0,
              mt: 1,
            }}
          >
            <Box
              component="i"
              className="bx bxs-error-circle"
              sx={{
                fontSize: '16px',
                color: 'var(--error)',
                mr: 0.8,
              }}
            />
            {formikProps.errors[fieldName]}
          </FormHelperText>
        ) : null}
      </FormControl>
    </Box>
  );
};

export default MobileField;
